import React, {useMemo, useState} from "react";
import {GridColDef} from "@mui/x-data-grid";
import {useNavigate} from "react-router-dom";
import {Box, Colors, Text} from "@bounce/shared/ui";
import {Button, ListDataGrid, ListPageHeader, RowActions, SearchInput} from "../../ui/components";
import {useGetTriggers} from "../../apis/requests/triggers/getTriggers";
import {ReachTrigger} from "../../apis/requests/triggers/types";
import {reachUrls} from "../../layout/menu-items";
import {triggerChannelsLabel, triggerTimingLabel, triggerUrl} from "./helpers";
import {openInNewTab} from "../../components/ResourcePanel";
import {InteractionChip, TriggerStatusChip} from "./TriggerBadges";
import {useConfirmDeleteTrigger} from "./useConfirmDeleteTrigger";
import {useTriggerTemplates} from "./useTriggerTemplates";

const TriggersPage = () => {
  const navigate = useNavigate();
  const {data, isLoading, error} = useGetTriggers();
  const {confirmDelete} = useConfirmDeleteTrigger();
  const {channels} = useTriggerTemplates();
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: 25});

  const filteredTriggers = useMemo(() => {
    const all: ReachTrigger[] = data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (trigger) =>
        trigger.name.toLowerCase().includes(q) ||
        (trigger.description ?? "").toLowerCase().includes(q) ||
        trigger.interaction_type.toLowerCase().includes(q)
    );
  }, [data, search]);

  const columns = useMemo<GridColDef<ReachTrigger>[]>(
    () => [
      {
        field: "name",
        headerName: "Trigger",
        flex: 2.5,
        renderCell: ({row}) => (
          <Box sx={{display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0, height: "100%"}}>
            <Text
              size={14}
              variant="inter600"
              sx={{color: Colors.blue._800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
            >
              {row.name}
            </Text>
            {row.description && (
              <Text
                size={12}
                sx={{color: Colors.grey._600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
              >
                {row.description}
              </Text>
            )}
          </Box>
        ),
      },
      {
        field: "interaction_type",
        headerName: "Interaction",
        flex: 1.8,
        renderCell: ({row}) => <InteractionChip interactionType={row.interaction_type} />,
      },
      {
        field: "conditions",
        headerName: "Conditions",
        flex: 1.1,
        minWidth: 110,
        valueGetter: (_value, row) => row.conditions.length,
        renderCell: ({row}) => <Text size={14}>{row.conditions.length}</Text>,
      },
      {
        field: "channels",
        headerName: "Channels",
        flex: 2,
        sortable: false,
        renderCell: ({row}) => <Text size={14}>{triggerChannelsLabel(row.conditions, channels)}</Text>,
      },
      {
        field: "timing",
        headerName: "Timing",
        flex: 1.2,
        sortable: false,
        renderCell: ({row}) => (
          <Text size={14} sx={{color: Colors.grey._600}}>
            {triggerTimingLabel(row.conditions)}
          </Text>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        renderCell: ({row}) => <TriggerStatusChip status={row.status} />,
      },
      {
        field: "principal",
        headerName: "Created by",
        flex: 1,
        minWidth: 110,
        filterable: false,
        renderCell: ({row}) => (
          <Text
            size={14}
            sx={{color: Colors.grey._700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
          >
            {row.principal ? row.principal.split("@")[0] : ""}
          </Text>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 72,
        sortable: false,
        filterable: false,
        renderCell: ({row}) => (
          <RowActions
            onEdit={() => navigate(triggerUrl(row.id))}
            onDelete={() => confirmDelete(row)}
            onOpenInNewTab={() => openInNewTab(triggerUrl(row.id))}
          />
        ),
      },
    ],
    [navigate, channels]
  );

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", height: "100%"}}>
      <ListPageHeader title="Triggers">
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPaginationModel((m) => ({...m, page: 0}));
          }}
          placeholder="Search triggers..."
        />
        <Button buttonType="contained-primary" size="small" onClick={() => navigate(reachUrls.triggersNew)}>
          + New trigger
        </Button>
      </ListPageHeader>
      {error ? (
        <Text size={14}>Failed to load triggers.</Text>
      ) : (
        <ListDataGrid
          rows={filteredTriggers}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          initialState={{sorting: {sortModel: [{field: "name", sort: "asc"}]}}}
          getRowUrl={(row) => triggerUrl(row.id)}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableColumnMenu={false}
          noRowsText={search.trim() ? "No triggers match your search" : "No triggers yet"}
        />
      )}
    </Box>
  );
};

export default TriggersPage;
